import React, { Fragment, useEffect } from "react";
import { useConfig } from "../../core/config";
import { useAnalytics } from "../../lib/analytics/analytics";
import { makeGA4Client } from "../../lib/analytics/ga4";
import { makeLinkedInClient } from "../../lib/analytics/linkedin";
import { makeGTMClient } from "../../lib/analytics/gtm";
import { makeHubspotClient } from "@/lib/analytics/hubspot";
import {
  makeOneSignalClient,
  OneSignalConfig,
} from "../../lib/analytics/onesignal";
import { useInitializeOneSignalFeatureFlag } from "@/core/splitio";

export function useBondCollectiveAnalytics() {
  const config = useConfig();
  const analytics = useAnalytics();
  const initializeOneSignalFeatureFlag = useInitializeOneSignalFeatureFlag();

  useEffect(() => {
    const variables = envVariables[config.environment];

    const hubspotClient = makeHubspotClient();

    analytics.addClient(hubspotClient);

    if (variables.gtmId) {
      const gtmClient = makeGTMClient(variables.gtmId);

      analytics.addClient(gtmClient);
    }

    if (variables.ga4TrackingId) {
      const ga4Client = makeGA4Client({
        trackingId: variables.ga4TrackingId,
        gaOptions: {
          name: "bondcollective",
        },
      });

      analytics.addClient(ga4Client);
    }

    // Bond Collective wants us to additionally send data to their own GA4 property which they use on their website
    if (variables.ga4TrackingId2) {
      const ga4Client = makeGA4Client({
        trackingId: variables.ga4TrackingId2,
        gaOptions: {
          name: "bondcollective-partner",
        },
      });

      analytics.addClient(ga4Client);
    }

    // LinkedIn insight tag
    if (variables.linkedInTrackingId) {
      const linkedInClient = makeLinkedInClient(variables.linkedInTrackingId);

      analytics.addClient(linkedInClient);
    }

    if (variables.oneSignalAppId && initializeOneSignalFeatureFlag) {
      const oneSignalConfig: Partial<OneSignalConfig> & { appId: string } = {
        appId: variables.oneSignalAppId,
      };
      const oneSignalClient = makeOneSignalClient(oneSignalConfig);
      analytics.addClient(oneSignalClient);
    }
  }, [config.environment, analytics, initializeOneSignalFeatureFlag]);
}

const envVariables = {
  development: {
    ga4TrackingId: "",
    ga4TrackingId2: "",
    linkedInTrackingId: "",
    gtmId: "",
    oneSignalAppId: "ae06a7d5-677c-4545-9064-4e5e5efb8be3",
  },
  staging: {
    ga4TrackingId: "G-7JHGFQ23GK",
    ga4TrackingId2: "",
    linkedInTrackingId: "",
    gtmId: "GTM-T44X6MH",
    oneSignalAppId: "ae06a7d5-677c-4545-9064-4e5e5efb8be3",
  },
  production: {
    ga4TrackingId: "G-BJE0XEE8F6",
    ga4TrackingId2: "G-KX4NYQ505G",
    linkedInTrackingId: "4456106",
    gtmId: "GTM-KDTK2ZJ",
    oneSignalAppId: "",
  },
};

export default function BondCollectiveAnalytics(): JSX.Element {
  useBondCollectiveAnalytics();

  return <Fragment></Fragment>;
}
